import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Users, 
  Plus,
  Copy,
  Check,
  ArrowLeft,
  Link as LinkIcon,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Mail,
  Building2,
  Upload,
  Image,
  X,
  Palette,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState({});
  const [copiedCode, setCopiedCode] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [newPartnerLogo, setNewPartnerLogo] = useState(null); // For logo during creation
  const [newPartnerLogoPreview, setNewPartnerLogoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const createLogoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    app_name: "",
    brand_color: "",
    hide_speak_to_agent: false
  });
  const navigate = useNavigate();

  const baseUrl = window.location.origin;

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await api.get("/admin/partners");
      setPartners(response.data.partners);
    } catch (error) {
      console.error("Error fetching partners:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPartner) {
        await api.put(`/admin/partners/${editingPartner.partner_id}`, formData);
        toast.success("Partner updated successfully");
      } else {
        // Create the partner first
        const response = await api.post("/admin/partners", formData);
        const newPartnerId = response.data.partner_id;
        
        // If a logo was selected, upload it now
        if (newPartnerLogo && newPartnerId) {
          const logoFormData = new FormData();
          logoFormData.append("file", newPartnerLogo);
          try {
            await api.post(`/admin/partners/${newPartnerId}/logo`, logoFormData, {
              headers: { "Content-Type": "multipart/form-data" }
            });
            toast.success("Partner created with logo successfully");
          } catch (logoError) {
            toast.success("Partner created, but logo upload failed");
            console.error("Logo upload error:", logoError);
          }
        } else {
          toast.success("Partner created successfully");
        }
      }
      setIsDialogOpen(false);
      setEditingPartner(null);
      setFormData({ name: "", email: "", company: "", app_name: "", brand_color: "", hide_speak_to_agent: false });
      setNewPartnerLogo(null);
      setNewPartnerLogoPreview(null);
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save partner");
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      email: partner.email || "",
      company: partner.company || "",
      app_name: partner.app_name || "",
      brand_color: partner.brand_color || "",
      hide_speak_to_agent: partner.hide_speak_to_agent || false
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (partnerId) => {
    if (!confirm("Are you sure you want to deactivate this partner?")) return;
    
    try {
      await api.delete(`/admin/partners/${partnerId}`);
      toast.success("Partner deactivated");
      fetchPartners();
    } catch (error) {
      toast.error("Failed to deactivate partner");
    }
  };

  const handleLogoUpload = async (partnerId, file) => {
    if (!file) return;
    
    setUploadingLogo(partnerId);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await api.post(`/admin/partners/${partnerId}/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Logo uploaded successfully");
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(null);
    }
  };

  const handleLogoDelete = async (partnerId) => {
    if (!confirm("Are you sure you want to delete this logo?")) return;
    
    try {
      await api.delete(`/admin/partners/${partnerId}/logo`);
      toast.success("Logo deleted");
      fetchPartners();
    } catch (error) {
      toast.error("Failed to delete logo");
    }
  };

  const copyLink = (code) => {
    const link = `${baseUrl}/register?ref=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const togglePartnerDetails = async (partnerId) => {
    if (expandedPartner === partnerId) {
      setExpandedPartner(null);
      return;
    }

    setExpandedPartner(partnerId);

    if (!partnerDetails[partnerId]) {
      try {
        const response = await api.get(`/admin/partners/${partnerId}`);
        setPartnerDetails(prev => ({
          ...prev,
          [partnerId]: response.data
        }));
      } catch (error) {
        console.error("Error fetching partner details:", error);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="partners-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/admin")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-8 h-8 text-[#547d91]" />
              Referral Partners
            </h2>
            <p className="text-slate-500 mt-1">
              Manage partners and their unique referral links
            </p>
          </div>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingPartner(null);
            setFormData({ name: "", email: "", company: "", app_name: "", brand_color: "", hide_speak_to_agent: false });
            setNewPartnerLogo(null);
            setNewPartnerLogoPreview(null);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#547d91] hover:bg-[#456a7a]">
              <Plus className="w-4 h-4 mr-2" />
              Add Partner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingPartner ? "Edit Partner" : "Add New Partner"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Partner Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="ABC Mortgage Brokers"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="partner@example.com"
                />
              </div>
              
              {/* White-Label Branding Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  White-Label Branding (Optional)
                </h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="app_name">Custom App Name</Label>
                    <Input
                      id="app_name"
                      value={formData.app_name}
                      onChange={(e) => setFormData({...formData, app_name: e.target.value})}
                      placeholder="My Property Tracker"
                    />
                    <p className="text-xs text-slate-500">This name will be shown in the sidebar for users referred by this partner</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="brand_color" className="flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Brand Color
                    </Label>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <input
                          type="color"
                          id="brand_color_picker"
                          value={formData.brand_color || "#547d91"}
                          onChange={(e) => setFormData({...formData, brand_color: e.target.value})}
                          className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer"
                        />
                      </div>
                      <Input
                        id="brand_color"
                        value={formData.brand_color}
                        onChange={(e) => setFormData({...formData, brand_color: e.target.value})}
                        placeholder="#547d91"
                        className="w-32 font-mono"
                      />
                      {formData.brand_color && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({...formData, brand_color: ""})}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">Custom color for buttons and accents in the partner's white-labeled app</p>
                  </div>
                  
                  {/* Hide Speak to Agent Toggle */}
                  <div className="flex items-center justify-between py-3 px-1 border rounded-lg bg-slate-50">
                    <div className="flex items-center gap-3">
                      <EyeOff className="w-4 h-4 text-slate-500" />
                      <div>
                        <Label htmlFor="hide_speak_to_agent" className="text-sm font-medium cursor-pointer">
                          Hide "Speak to an Agent"
                        </Label>
                        <p className="text-xs text-slate-500">Hide the contact link for users referred by this partner</p>
                      </div>
                    </div>
                    <Switch
                      id="hide_speak_to_agent"
                      checked={formData.hide_speak_to_agent}
                      onCheckedChange={(checked) => setFormData({...formData, hide_speak_to_agent: checked})}
                    />
                  </div>
                  
                  {/* Logo Upload - Show for both new and editing partner */}
                  <div className="space-y-2">
                    <Label>Partner Logo</Label>
                    <div className="flex items-center gap-4">
                      {/* Current Logo Preview */}
                      <div className="w-16 h-16 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
                        {editingPartner ? (
                          // Editing mode - show uploaded logo
                          editingPartner.logo_filename ? (
                            <img 
                              src={`${API_URL}/api/partner-logos/${editingPartner.logo_filename}`}
                              alt={editingPartner.name}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Image className="w-6 h-6 text-slate-300" />
                          )
                        ) : (
                          // Create mode - show selected file preview
                          newPartnerLogoPreview ? (
                            <img 
                              src={newPartnerLogoPreview}
                              alt="Logo preview"
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Image className="w-6 h-6 text-slate-300" />
                          )
                        )}
                      </div>
                      
                      {/* Upload/Delete Buttons */}
                      <div className="flex flex-col gap-2">
                        {editingPartner ? (
                          // Editing mode - upload directly to server
                          <>
                            <input
                              type="file"
                              accept="image/*"
                              id="logo-upload-dialog"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (file && editingPartner) {
                                  await handleLogoUpload(editingPartner.partner_id, file);
                                  // Refresh the editing partner data
                                  const updatedPartners = await api.get("/admin/partners");
                                  const updated = updatedPartners.data.partners?.find(p => p.partner_id === editingPartner.partner_id);
                                  if (updated) setEditingPartner(updated);
                                }
                                e.target.value = '';
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById('logo-upload-dialog')?.click()}
                              disabled={uploadingLogo === editingPartner.partner_id}
                              className="gap-2"
                            >
                              {uploadingLogo === editingPartner.partner_id ? (
                                <span className="animate-spin">⟳</span>
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                              {editingPartner.logo_filename ? "Change Logo" : "Upload Logo"}
                            </Button>
                            
                            {editingPartner.logo_filename && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  await handleLogoDelete(editingPartner.partner_id);
                                  setEditingPartner({...editingPartner, logo_filename: null});
                                }}
                                className="gap-2 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                                Remove Logo
                              </Button>
                            )}
                          </>
                        ) : (
                          // Create mode - store file for upload after creation
                          <>
                            <input
                              type="file"
                              accept="image/*"
                              ref={createLogoInputRef}
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setNewPartnerLogo(file);
                                  // Create preview URL
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setNewPartnerLogoPreview(reader.result);
                                  };
                                  reader.readAsDataURL(file);
                                }
                                e.target.value = '';
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => createLogoInputRef.current?.click()}
                              className="gap-2"
                            >
                              <Upload className="w-4 h-4" />
                              {newPartnerLogo ? "Change Logo" : "Select Logo"}
                            </Button>
                            
                            {newPartnerLogo && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setNewPartnerLogo(null);
                                  setNewPartnerLogoPreview(null);
                                }}
                                className="gap-2 text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                                Remove
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">Recommended: Square image, PNG or JPG format</p>
                  </div>
                </div>
              </div>
              
              <Button type="submit" className="w-full bg-[#547d91] hover:bg-[#456a7a]">
                {editingPartner ? "Save Changes" : "Create Partner"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#547d91]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#547d91]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {partners.filter(p => p.is_active).length}
                </p>
                <p className="text-sm text-slate-500">Active Partners</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <LinkIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {partners.reduce((acc, p) => acc + (p.user_count || 0), 0)}
                </p>
                <p className="text-sm text-slate-500">Total Referrals</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partners Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Partner List</CardTitle>
        </CardHeader>
        <CardContent>
          {partners.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No partners added yet</p>
              <p className="text-sm text-slate-400 mt-1">Click "Add Partner" to create your first referral partner</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Partner</TableHead>
                    <TableHead>Branding</TableHead>
                    <TableHead>Referral Link</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.map((partner) => (
                    <>
                      <TableRow key={partner.partner_id} className="hover:bg-slate-50">
                        <TableCell 
                          className="cursor-pointer"
                          onClick={() => togglePartnerDetails(partner.partner_id)}
                        >
                          {expandedPartner === partner.partner_id ? (
                            <ChevronUp className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{partner.name}</p>
                            {partner.company && (
                              <p className="text-sm text-slate-500">{partner.company}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {partner.logo_filename ? (
                              <img 
                                src={`${API_URL}/api/partner-logos/${partner.logo_filename}`}
                                alt={partner.name}
                                className="w-8 h-8 object-contain rounded"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
                                <Image className="w-4 h-4 text-slate-400" />
                              </div>
                            )}
                            {partner.brand_color && (
                              <div 
                                className="w-6 h-6 rounded border border-slate-200"
                                style={{ backgroundColor: partner.brand_color }}
                                title={partner.brand_color}
                              />
                            )}
                            {partner.app_name ? (
                              <span className="text-sm text-slate-600">{partner.app_name}</span>
                            ) : (
                              <span className="text-sm text-slate-400 italic">Default</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                              ?ref={partner.referral_code}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyLink(partner.referral_code)}
                            >
                              {copiedCode === partner.referral_code ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{partner.user_count || 0} users</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={partner.is_active ? "default" : "destructive"}>
                            {partner.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              ref={fileInputRef}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file && fileInputRef.current?.dataset?.partnerId) {
                                  handleLogoUpload(fileInputRef.current.dataset.partnerId, file);
                                }
                                e.target.value = '';
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (fileInputRef.current) {
                                  fileInputRef.current.dataset.partnerId = partner.partner_id;
                                  fileInputRef.current.click();
                                }
                              }}
                              disabled={uploadingLogo === partner.partner_id}
                              title="Upload Logo"
                            >
                              {uploadingLogo === partner.partner_id ? (
                                <span className="animate-spin">⟳</span>
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                            </Button>
                            {partner.logo_filename && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleLogoDelete(partner.partner_id)}
                                className="text-red-500 hover:text-red-700"
                                title="Remove Logo"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(partner)}
                              title="Edit Partner"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {partner.is_active && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(partner.partner_id)}
                                className="text-red-500 hover:text-red-700"
                                title="Deactivate Partner"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedPartner === partner.partner_id && partnerDetails[partner.partner_id] && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-slate-50 p-4">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="font-semibold text-slate-700">Referred Users</h4>
                                <div className="text-sm text-slate-500">
                                  Full link: <code className="bg-white px-2 py-1 rounded border text-xs">
                                    {baseUrl}/register?ref={partner.referral_code}
                                  </code>
                                </div>
                              </div>
                              {partnerDetails[partner.partner_id].users.length === 0 ? (
                                <p className="text-slate-500 text-sm">No users referred yet</p>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {partnerDetails[partner.partner_id].users.map((user) => (
                                    <div key={user.user_id} className="bg-white p-3 rounded-lg border border-slate-200">
                                      <p className="font-medium text-slate-900">{user.name}</p>
                                      <p className="text-sm text-slate-500 flex items-center gap-1">
                                        <Mail className="w-3 h-3" /> {user.email}
                                      </p>
                                      <div className="flex items-center justify-between mt-2 text-sm">
                                        <span className="text-slate-500 flex items-center gap-1">
                                          <Building2 className="w-3 h-3" /> {user.property_count || 0} properties
                                        </span>
                                        <span className="text-slate-400">
                                          {formatDate(user.created_at)}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
